/**
 * The read-only conversational agent — canned, grounded answers shared by both providers.
 *
 * Moved verbatim out of `demo.ts` so the snapshot provider quotes the **real** served figures
 * (requirement R-012) instead of the demo constants: every anchor the copy cites arrives in
 * `AgentAnchors` rather than being read off a module-level demo table.
 *
 * Wording that used to assume "a representative sample" is derived instead — when the served
 * field covers the whole held portfolio (`skus.length >= site.heldSKUs`) the sample caveats
 * drop out. Same for the non-mover unit: `lastMovedQ` counts review windows, whose length is
 * the drop's own (4 weeks on a single-month drop), so the copy never names a quarter.
 *
 * Pick faces are estimated from the boxes a SKU holds — no drop has carried a pick-location
 * feed — and the conversion is a depth scale, not the site's slotting, so no answer quotes a
 * face COUNT: they quote a share of the field's own estimated depth (`depthPct`), which is true
 * whatever the scale is. `site.pickCap` is a planning-deck figure on a different basis and is
 * never mixed in. See `DEPTH_BASIS` in `math.ts`.
 *
 * "Hasn't shipped" is read over the sales window and "still holds pick faces" off the stock
 * position, and on a real drop those are months apart — so every answer that puts the two
 * together carries `BASIS`, the same dates the rail caption and the report narratives name.
 *
 * Money is labelled in the drop's own reporting currency (`site.ntsCurrency`), through the same
 * formatter the widgets use — the agent quoting "£" beside a widget reading "€" was the whole
 * bug. The capital lever's `£m` is a planning-deck figure in its own currency, not a served one.
 */

import {
  CAP_YEARS,
  cumAt,
  defaultRoll,
  DEPTH_BASIS,
  depthPct,
  firstBreachYear,
  heldAt,
  leverPool,
  MAX_UNITS,
  UNIT_COST,
  UNIT_SKU,
  UNIT_SPACE,
  unitLead,
} from "./math";
import { fmtMoneyShort } from "./money";
import { annualisedNote, BASIS, MONTH_SLOTS, simulated } from "./served";
import type {
  AgentContext,
  AgentReply,
  CapacityModel,
  Channel,
  Lever,
  LeverFamily,
  LeverId,
  ReloStop,
  RollPlan,
  Site,
  Sku,
  SpaceLeverId,
} from "./types";

/** Everything the canned answers quote. A provider passes its own served figures. */
export interface AgentAnchors {
  skus: readonly Sku[];
  site: Site;
  capacity: CapacityModel;
  levers: readonly Lever[];
  families: Record<SpaceLeverId, readonly LeverFamily[]>;
  channels: readonly Channel[];
  reloStops: readonly ReloStop[];
}

function agentBtn(label: string, call: string): string {
  return `<button class="agent-do" data-do="${call}">${label} →</button>`;
}

const sum = (set: readonly Sku[], f: (s: Sku) => number) => set.reduce((a, s) => a + f(s), 0);
/** One decimal, two when the share would otherwise round to a flat 0.0%. */
const pctS = (p: number) => (p >= 0.1 ? p.toFixed(1) : p.toFixed(2));
/** Boxes/month, which is fractional at the tail of a real drop. */
const velS = (v: number) => v.toLocaleString(undefined, { maximumFractionDigits: v < 10 ? 1 : 0 });

function fieldAnswerBody(question: string, ctx: AgentContext, a: AgentAnchors): AgentReply {
  const { skus: SKUS, site: SITE } = a;
  const q = question.toLowerCase();
  /** Every money figure below, in the currency the drop reports in. */
  const money = (v: number) => fmtMoneyShort(v, SITE.ntsCurrency);
  const selIds = new Set(ctx.sel ?? []);
  const selSkus = selIds.size ? SKUS.filter((s) => selIds.has(s.id)) : null;
  /** True when the served field is the whole held portfolio, not a legibility sample. */
  const whole = SKUS.length >= SITE.heldSKUs;
  /** Share of site NTS a set carries — the same read as the right panel. */
  const ntsPct = (set: readonly Sku[]) => (sum(set, (s) => s.value) / SITE.nts) * 100;
  /**
   * How broadly a set ships, as the median accounts per SKU.
   *
   * NOT a union, and deliberately not presented as one. The payload carries an account COUNT per
   * SKU and never account identities, so the set's true distinct reach is not computable here.
   * This previously summed those counts and clamped the total at `SITE.accounts`, which
   * double-counted every account buying more than one SKU and turned any sizeable selection into
   * "ships to all of the site's accounts" — a fabricated 100%, with the clamp making it look
   * deliberate. The median is served, true, and answers what the sentence is actually asking.
   *
   * ponytail: median per SKU. To state real distinct reach, the build must serve distinct account
   * ids (or a precomputed union count) per material — it has them in the sales feed.
   */
  const medianReach = (set: readonly Sku[]) => {
    if (!set.length) {
      return 0;
    }
    const counts = set.map((s) => s.accounts).sort((a, b) => a - b);
    return counts[counts.length >> 1] ?? 0;
  };
  /** Σ estimated depth across the field — the denominator every face share is read against. */
  const baseFaces = sum(SKUS, (s) => s.faces);
  /** A set's footprint as a share of that depth. Never a face count — see the file header. */
  const depth = (set: readonly Sku[]) =>
    depthPct(
      sum(set, (s) => s.faces),
      baseFaces,
    );
  // BASIS is hydrated in place, so both clauses are composed per answer, never at module scope.
  /** The window the shipped figures are read over — "the review window" is not a date. */
  const inWindow = `in <b>${BASIS.salesSpan}</b>`;
  /** Said wherever an answer puts "hasn't shipped" beside "still holds pick faces". */
  const heldBasis = BASIS.inventoryAsOf
    ? ` The pick faces are the stock position as at <b>${BASIS.inventoryAsOf}</b>${BASIS.gap ? `, ${BASIS.gap} that window` : ""} — so a SKU that shipped after the window is still counted here.`
    : " No dated stock position accompanies this drop, so the pick faces cannot be dated against that window.";
  /** Channel mix of a set, weighted by monthly velocity — the Channel lens' read, ranked. */
  const channelShare = (set: readonly Sku[]) => {
    const units = sum(set, (s) => Math.max(s.vel, 1)) || 1;
    return a.channels
      .map((c, i) => ({
        label: c.label,
        pct: (sum(set, (s) => (s.chan[i] ?? 0) * Math.max(s.vel, 1)) / units) * 100,
      }))
      .sort((x, y) => y.pct - x.pct);
  };

  if (q.includes("problematic")) {
    return {
      html:
        "Your most problematic SKUs sit <b>bottom-left</b> — slow-moving and low-value, yet still holding pick faces. I've selected the <b>" +
        SKUS.filter((s) => s.worth).length +
        "</b> worst; each is a relocation, conversion or de-list candidate. " +
        agentBtn("Open Capacity scenarios", "cap"),
      action: "selectWorth",
      nav: "cap",
    };
  }
  // "mismatch" is still matched, because it is what people ask — and the answer's job is now to
  // say the question cannot be answered from this data, rather than to invent a count for it.
  if (q.includes("mismatch") || q.includes("slot") || q.includes("automation")) {
    const lane = SKUS.filter((s) => s.pickReco === "A-frame");
    const auto = SKUS.filter((s) => s.pickReco === "OSR automation");
    return {
      html:
        `<b>${(lane.length + auto.length).toLocaleString()} SKUs</b> move fast enough — and are held deep enough — to belong in a scarce pick location: <b>${lane.length.toLocaleString()}</b> in a dedicated A-frame lane and <b>${auto.length.toLocaleString()}</b> in automation. I've highlighted them on the field. ` +
        "<b>I can't tell you which are mismatched.</b> That needs a pick-location feed, and this data " +
        "has none — so where each SKU sits today is unknown here, and the recommendation is read " +
        "off its measured velocity and depth. Check the list against the WMS. " +
        agentBtn("Generate slotting review", "storage"),
      action: "selectAutomationReco",
      nav: "storage",
    };
  }
  if (q.includes("moved") || q.includes("non-mover")) {
    const dead = SKUS.filter((s) => s.lastMovedQ >= 1);
    const npi = dead.filter((s) => s.isNew).length;
    return {
      html:
        (whole
          ? `<b>${dead.length} SKUs</b> didn't ship ${inWindow} but still hold pick faces — <b>${depth(dead)}</b> of the field's estimated pick-face depth.`
          : `In this representative sample, <b>${dead.length.toLocaleString()} SKUs</b> didn't ship ${inWindow} but still hold pick faces — across the full site that's <b>${SITE.deadCount.toLocaleString()} non-movers</b>.`) +
        heldBasis +
        " " +
        (npi
          ? `<b>${npi.toLocaleString()}</b> of them (${Math.round((npi / dead.length) * 100)}%) are NPI-flagged — newly launched and not yet shipping, not dead stock, and protected from every lever. The other <b>${(dead.length - npi).toLocaleString()}</b> are the real candidates. `
          : "") +
        "I've highlighted them; relocate or de-list to free space without touching anything that moves. " +
        agentBtn("Generate non-mover report", "nonmover"),
      action: "selectNonMovers",
      nav: "nonmover",
    };
  }
  // "Show me the slow, low-value Rx that a regional hub could serve"
  if (q.includes("hub") || q.includes("regional")) {
    const set = SKUS.filter((s) => s.type === "Rx" && s.worth);
    const vels = set.map((s) => s.vel).sort((x, y) => x - y);
    const median = vels[Math.floor(vels.length / 2)] ?? 0;
    return {
      html:
        `<b>${set.length.toLocaleString()} slow, low-value Rx</b>${whole ? "" : " in this sample"} are hub candidates — median velocity <b>${velS(median)}/mo</b> ${inWindow}, sitting on <b>${depth(set)}</b> of the field's estimated pick-face depth (${DEPTH_BASIS}) for ${money(sum(set, (s) => s.value))} a year, just <b>${pctS(ntsPct(set))}%</b> of site NTS.${heldBasis} I've selected them: thin, dispersed demand a regional hub can serve on a longer lead time. At site scale the relocation pool is <b>≈ ${leverPool(a.families.relo, 0).toLocaleString()} SKU-slots</b> at the non-mover threshold, and looser velocity stops sweep in more. ` +
        agentBtn("Open SKU Relocation", "cap"),
      action: "selectReloRx",
      nav: "cap",
    };
  }
  // "Is this SKU slow because it's dead, or because it's new?"
  if (q.includes("dead") || q.includes("new")) {
    const dead = SKUS.filter((s) => s.lastMovedQ >= 1);
    const deadNpi = dead.filter((s) => s.isNew).length;
    const fresh = SKUS.filter((s) => s.isNew);
    const prem = SKUS.filter((s) => s.premiumSlow);
    return {
      html:
        `Two different stories share the bottom-left, and the field separates them. <b>${dead.length.toLocaleString()} SKUs</b> didn't ship at all ${inWindow}${deadNpi ? `, but <b>${deadNpi.toLocaleString()}</b> of those (${Math.round((deadNpi / dead.length) * 100)}%) carry the NPI flag — launched, still ramping, and not dead stock. That leaves <b>${(dead.length - deadNpi).toLocaleString()}</b> genuinely dormant` : ""}. <b>${fresh.length.toLocaleString()} SKUs</b> in all carry the gold halo, so low velocity is expected — they are excluded from every rationalisation lever. A further <b>${prem.length.toLocaleString()}</b> are slow but premium (green): ${money(sum(prem, (s) => s.value))} a year between them, ${pctS(ntsPct(prem))}% of site NTS on low volume. Check the halo${simulated("launch") ? " — the launch dates on the SKU profile are simulated in this drop, so the halo is the reliable read" : " and the launch date"} before calling a SKU dead. ` +
        agentBtn("Generate non-mover report", "nonmover"),
      nav: "nonmover",
    };
  }
  // "Which Dx are we overstocking but barely shipping?"
  if (
    q.includes("overstock") ||
    (q.includes("dx") && (q.includes("ship") || q.includes("barely")))
  ) {
    const set = SKUS.filter((s) => s.type === "Dx" && s.worth);
    const topVel = set.reduce((m, s) => Math.max(m, s.vel), 0);
    const npiDx = SKUS.filter((s) => s.type === "Dx" && s.isNew).length;
    return {
      html:
        `<b>${set.length.toLocaleString()} Dx fitting SKUs</b> shipped <b>${velS(topVel)}/mo or less</b> ${inWindow} yet hold <b>${Math.round(sum(set, (s) => s.inv)).toLocaleString()} boxes</b> — <b>${depth(set)}</b> of the field's estimated pick-face depth.${heldBasis} I've selected them — they sit high on the right-hand axis, which is the point of the Dx dual-axis view: Dx carries no NTS, so it is judged on the inventory it consumes (right axis), not the money it earns (left). It is a tight cohort${npiDx ? `, because ${npiDx.toLocaleString()} deep-stocked Dx are NPI-flagged and protected while they ramp` : ""}. At site scale <b>≈ ${leverPool(a.families.conv, 0).toLocaleString()} candidate SKUs</b> are in scope for Rx→Dx conversion — thin the fitting range, ship an Rx sample packet instead. ` +
        agentBtn("Open Rx → Dx Conversion", "cap"),
      action: "selectOverstockDx",
      nav: "cap",
    };
  }
  // "If we relocate these, which customers and channels are affected?"
  if (q.includes("customer") || q.includes("channel") || q.includes("affected")) {
    const set = selSkus ?? SKUS.filter((s) => s.worth);
    const markets = new Set(set.flatMap((s) => s.geo.map((g) => g.name))).size;
    const [top = { label: "", pct: 0 }, second = { label: "", pct: 0 }] = channelShare(set);
    const lead = selSkus
      ? `The <b>${set.length.toLocaleString()} SKUs</b> you have selected`
      : `Nothing is selected, so I've read the <b>${set.length.toLocaleString()}</b> relocation candidates on the field — they`;
    return {
      html: `${lead} each ship to a median of <b>${medianReach(set).toLocaleString()} accounts</b>, out of the ${SITE.accounts.toLocaleString()} this site serves, spread across <b>${markets} markets</b>. The mix leans <b>${top.label} ${top.pct.toFixed(0)}%</b> and <b>${second.label} ${second.pct.toFixed(0)}%</b> — those two carry ${(top.pct + second.pct).toFixed(0)}% of the volume, so they absorb most of the lead-time change; relocation moves where the stock sits, not what those accounts can order. The <b>Customer</b> lens maps only the accounts receiving the selection, and the <b>Channel</b> lens shows its red share of each channel — both re-read whatever is selected.`,
    };
  }
  // "What revenue is at risk if we de-list this cluster?"
  if (
    q.includes("revenue") ||
    q.includes("de-list") ||
    q.includes("delist") ||
    q.includes("at risk")
  ) {
    if (!selSkus) {
      return {
        html: `Select the cluster first — drag a box round it on the plot, or ask me for the problematic SKUs — and I'll read its share of the site's ${money(SITE.nts)} NTS before anything gets de-listed.`,
      };
    }
    const slow = selSkus.filter((s) => s.worth);
    const prem = selSkus.filter((s) => s.premiumSlow);
    const dx = selSkus.filter((s) => s.type === "Dx");
    return {
      html: `The <b>${selSkus.length.toLocaleString()} SKUs</b> selected carry <b>${money(sum(selSkus, (s) => s.value))} a year</b> — <b>${pctS(ntsPct(selSkus))}%</b> of site NTS — on <b>${depth(selSkus)}</b> of the field's estimated pick-face depth (${DEPTH_BASIS}). De-list the whole cluster and all of that is at risk. The defensible part is the <b>${slow.length.toLocaleString()} slow, low-value</b> ones: ${money(sum(slow, (s) => s.value))}, only <b>${pctS(ntsPct(slow))}%</b> of site NTS, freeing <b>${depth(slow)}</b> of that depth.${prem.length ? ` Keep the <b>${prem.length.toLocaleString()}</b> slow-but-premium ones — ${money(sum(prem, (s) => s.value))} a year on low volume; velocity alone would mislead you there.` : ""}${dx.length ? ` The <b>${dx.length.toLocaleString()} Dx</b> in the selection carry no NTS at all — their case is space, not revenue.` : ""}`,
    };
  }
  // The catch-all offers only what the data can actually answer. It advertised "pick-location
  // mismatches" — the one claim this surface no longer makes, since no drop knows where a SKU sits
  // today. A menu that offers a capability the answer then refuses is worse than a short menu.
  return {
    html: "I can point you at the problematic SKUs, the ones whose velocity and depth suit automation or an A-frame lane, or the SKUs that haven't moved in the review window — try one of the suggestions below.",
  };
}

/**
 * Appends the annualisation basis to any answer that quotes a money-per-year figure.
 *
 * Applied at the single return path rather than inside each branch: there are a dozen branches, a
 * new one quoting money would otherwise silently omit the basis, and this way it cannot. Keyed on
 * the rendered text so it fires exactly where a per-year figure appears, and it self-disables on a
 * twelve-month drop because `annualisedNote` returns empty there.
 */
function withMoneyBasis(reply: AgentReply): AgentReply {
  const note = annualisedNote(MONTH_SLOTS.length);
  if (!note || !/ a year/.test(reply.html) || reply.html.includes(note)) {
    return reply;
  }
  return { ...reply, html: `${reply.html} ${note}` };
}

/**
 * Field-surface answers.
 *
 * Deliberately NOT applied to `capacityAnswer`: its money figures are the automation lever's
 * capital costs, which are a different basis entirely — appending an NTS annualisation note there
 * would be a false statement, not a missing one.
 */
export function fieldAnswer(question: string, ctx: AgentContext, a: AgentAnchors): AgentReply {
  return withMoneyBasis(fieldAnswerBody(question, ctx, a));
}

export function capacityAnswer(question: string, ctx: AgentContext, a: AgentAnchors): AgentReply {
  const NPI_FAM = a.capacity.npiFamilies;
  const CAPD = a.capacity.capd;
  const q = question.toLowerCase();
  // The screen always sends its year; the fallback is derived rather than the old hardcoded 2027,
  // so an answer given without context still names a year that means something on this plan.
  const year =
    ctx.year ?? firstBreachYear(a.capacity) ?? a.capacity.years?.[0] ?? CAP_YEARS[0] ?? 2026;
  const roll: RollPlan = ctx.roll ?? defaultRoll(NPI_FAM);
  const stop = a.reloStops[ctx.reloStopIndex ?? 0] ?? a.reloStops[0];
  const effHeld = ctx.held ?? heldAt(roll, year, a.capacity);
  const ceiling = ctx.ceiling ?? CAPD.ceil;
  const effPct = (effHeld / ceiling) * 100;
  const relo = leverPool(a.families.relo, ctx.reloStopIndex ?? 0);
  const units = (slots: number) => Math.max(1, Math.ceil(slots / UNIT_SKU));
  /** Capital line for closing `slots` with automation — the lever the others are cheaper than. */
  const capitalFor = (slots: number) => {
    const u = units(slots);
    return `<b>${u} automation unit${u > 1 ? "s" : ""}</b> — £${(u * UNIT_COST).toFixed(1)}m, ${(u * UNIT_SPACE).toLocaleString()} m² and ${unitLead(u)}${u > MAX_UNITS ? `, beyond the ${MAX_UNITS}-unit floor envelope` : ""}`;
  };
  // "What's the cheapest way back under the ceiling this year?"
  if (q.includes("cheap") || q.includes("lowest cost") || q.includes("least cost")) {
    const gap = effHeld - ceiling;
    if (gap <= 0) {
      return {
        html: `In ${year} you're already under — held <b>${effHeld.toLocaleString()}</b> of ${ceiling.toLocaleString()} (${effPct.toFixed(0)}%), <b>${(-gap).toLocaleString()} slots</b> of headroom — so the cheapest move is none. If you want margin before the next intake, relocation at the <b>${stop?.short ?? ""}</b> threshold frees ≈ ${relo.toLocaleString()} more at zero capital.`,
      };
    }
    const title = (leverId: LeverId) => a.levers.find((l) => l.id === leverId)?.title ?? leverId;
    const pools = [
      { name: title("relo"), n: relo },
      { name: title("conv"), n: leverPool(a.families.conv, 0) },
      { name: title("pack"), n: leverPool(a.families.pack, 0) },
    ].sort((x, y) => y.n - x.n);
    const combo: string[] = [];
    let freed = 0;
    for (const p of pools) {
      if (freed >= gap) {
        break;
      }
      combo.push(p.name);
      freed += p.n;
    }
    return {
      html: `The three space levers need no capital, so they're always the cheap end. In ${year} you're <b>${gap.toLocaleString()} SKUs over</b> the ${ceiling.toLocaleString()} ceiling (held ${effHeld.toLocaleString()}, ${effPct.toFixed(0)}%). The pools: ${pools.map((p) => `<b>${p.name}</b> ≈ ${p.n.toLocaleString()}`).join(", ")} SKU-slots. <b>${combo.join(" + ")}</b> alone frees ≈ ${freed.toLocaleString()} — enough on its own, at £0 capital. Closing the same gap with automation costs ${capitalFor(gap)}. So: take ${combo.join(" + ")}, stage the NPI rollout for what's left, and keep the capital lever for the gap nothing else reaches.`,
    };
  }
  // "If I stage MAX RU A 2W across 2027–29 instead of all at once, do we stay under the ceiling?"
  if (
    (q.includes("stage") || q.includes("phase") || q.includes("spread") || q.includes("across")) &&
    (q.includes("instead") ||
      q.includes("2029") ||
      q.includes("–29") ||
      q.includes("-29") ||
      NPI_FAM.some((f) => q.includes(f.name.toLowerCase())))
  ) {
    const fam =
      NPI_FAM.find((f) => q.includes(f.name.toLowerCase())) ??
      NPI_FAM.reduce((x, f) => (f.total > x.total ? f : x));
    const endYear = CAP_YEARS[CAP_YEARS.length - 1] ?? year;
    const others = (yr: number) =>
      NPI_FAM.filter((f) => f.id !== fam.id).reduce((x, f) => x + cumAt(roll, f.id, yr), 0);
    const room = (yr: number) => Math.max(0, ceiling - CAPD.base2026 - others(yr));
    const roomRel = room(fam.rel);
    const roomEnd = room(endYear);
    const over = fam.total - roomEnd;
    const span = `${fam.rel}–${String(endYear).slice(2)}`;
    if (over <= 0) {
      return {
        html: `Yes. The ${ceiling.toLocaleString()} ceiling less the ${CAPD.base2026.toLocaleString()} already held and ${others(endYear).toLocaleString()} from the other NPI families leaves room for <b>${roomEnd.toLocaleString()}</b> by ${endYear} — more than <b>${fam.name}</b>'s ${fam.total.toLocaleString()}, so spreading it across ${span} stays under with <b>${(-over).toLocaleString()} slots</b> to spare. Keep ${fam.rel} itself at or below <b>${roomRel.toLocaleString()}</b>, which is all that year has room for.`,
      };
    }
    return {
      html: `Partly — staging buys the release year, not the horizon. The ${ceiling.toLocaleString()} ceiling less the ${CAPD.base2026.toLocaleString()} already held and ${others(fam.rel).toLocaleString()} from the other NPI families leaves room for <b>${roomRel.toLocaleString()}</b> of <b>${fam.name}</b>'s ${fam.total.toLocaleString()} in ${fam.rel}: stage it to that and ${fam.rel} stays under. But the ceiling doesn't move, and by ${endYear} the other families have taken their share too — the most ${fam.name} can ever hold under it is <b>${roomEnd.toLocaleString()}</b>, so spreading all ${fam.total.toLocaleString()} across ${span} still ends <b>${over.toLocaleString()} SKUs over</b>. Stage to ~${roomRel.toLocaleString()} in ${fam.rel}, then cover the remaining ${over.toLocaleString()} with the space levers (relocation alone frees ≈ ${relo.toLocaleString()} at the ${stop?.short ?? ""} threshold) or ${capitalFor(over)}.`,
    };
  }
  if (q.includes("2027") || q.includes("break")) {
    // The breach year is whichever year the scheduled rollout first crosses the ceiling.
    const breach = CAP_YEARS.find((y) => heldAt(defaultRoll(NPI_FAM), y, a.capacity) > CAPD.ceil);
    const by = breach ?? year;
    const landing = NPI_FAM.filter((f) => f.rel === by);
    const held = heldAt(defaultRoll(NPI_FAM), by, a.capacity);
    return {
      html: breach
        ? `${by} is the breach year: the DC lands ${landing.map((f) => `<b>${f.name}</b> (${f.total.toLocaleString()})`).join(" and ")} together — +${landing.reduce((x, f) => x + f.total, 0).toLocaleString()} SKUs in one year — pushing held to ~${held.toLocaleString()} vs the ${CAPD.ceil.toLocaleString()} ceiling, about <b>${Math.round((held / CAPD.ceil) * 100)}%</b>.`
        : `On the scheduled rollout nothing breaches: by ${CAP_YEARS[CAP_YEARS.length - 1]} held reaches ${heldAt(defaultRoll(NPI_FAM), CAP_YEARS[CAP_YEARS.length - 1] ?? by, a.capacity).toLocaleString()} against the ${CAPD.ceil.toLocaleString()} ceiling.`,
    };
  }
  if (q.includes("ceiling") || q.includes("under") || q.includes("back") || q.includes("fix")) {
    const biggest = NPI_FAM.reduce((x, f) => (f.total > x.total ? f : x));
    return {
      html: `Three ways: <b>stage ${biggest.name}</b> more slowly (phase its ${biggest.total.toLocaleString()} across ${biggest.rel}–${String(CAP_YEARS[CAP_YEARS.length - 1] ?? biggest.rel).slice(2)} on the left), <b>relocate or convert</b> slow SKUs with the levers on the right, or <b>add automation capacity</b>.`,
    };
  }
  if (
    q.includes("rollout") ||
    q.includes("npi") ||
    q.includes("this year") ||
    q.includes("stage")
  ) {
    const intro = NPI_FAM.filter((f) => f.rel <= year)
      .map((f) => f.name + " " + cumAt(roll, f.id, year).toLocaleString())
      .join(", ");
    return {
      html: `In ${year}, cumulative introductions: ${intro}. Held <b>${effHeld.toLocaleString()}</b> of ${ceiling.toLocaleString()} (${effPct.toFixed(0)}%).`,
    };
  }
  if (q.includes("relocat")) {
    // The loosest stop is whatever the drop's own window can resolve — never a fixed "≤100/yr",
    // and there is nothing to pull looser to once the slider is already there.
    const last = a.reloStops.length - 1;
    const atLoosest = (ctx.reloStopIndex ?? 0) >= last;
    const loosest = a.reloStops[last]?.short;
    return {
      html: `At the current <b>${stop?.short ?? ""}</b> threshold, relocation frees <b>≈ ${leverPool(a.families.relo, ctx.reloStopIndex ?? 0).toLocaleString()} SKU-slots</b> across ${a.families.relo.length} brand families. ${atLoosest ? "That is the loosest threshold this drop's window can resolve — anything slower is a non-mover already in the pool." : `Pull the velocity slider looser${loosest ? ` (up to ${loosest})` : ""} to sweep in slower movers and free more.`} Open Review to see the family roll-up.`,
    };
  }
  if (q.includes("convert") || q.includes("dx")) {
    return {
      html: `Rx→Dx conversion swaps low-running Dx fitting sets for a small Rx packet — <b>≈ ${leverPool(a.families.conv, 0).toLocaleString()} candidate SKUs</b> across ${a.families.conv.length} families. Also helps EU MDR/UDI compliance.`,
    };
  }
  return {
    html: `I can talk through the ${year} capacity position, the NPI rollout, or any of the four levers — try a suggestion above, or ask about a specific product like ${NPI_FAM.reduce((x, f) => (f.total > x.total ? f : x)).name}.`,
  };
}
